// eslint-disable-next-line no-restricted-imports -- package name legitimately ends in `.js`
import { driver } from 'driver.js'
// eslint-disable-next-line no-restricted-imports -- driver.js ships its stylesheet here
import 'driver.js/dist/driver.css'

import { computed, nextTick, ref } from 'vue'

import { useRouter } from 'vue-router'

import { defineStore, storeToRefs } from 'pinia'

import { useFirestore } from 'vuefire'

import { doc, setDoc } from 'firebase/firestore'

import useOuraAuth from '#web-oura-tools/auth'

import { useAppStore } from '#ourahub-intra/stores/application'
import { useServicesStore } from '#ourahub-intra/stores/services'

import type { Category, Service } from '#ourahub-intra/types/services'

import '#ourahub-intra/styles/introTour.css'

type DriverInstance = ReturnType<typeof driver>

interface TourStep {
  // CSS selector of the element to highlight
  element?: string
  // Whether the demo service dialog must be open for this step.
  demo?: boolean
  // Which view the demo dialog shows (defaults to settings).
  demoView?: 'info' | 'settings'
  // Whether the app / tool type dropdown in the demo settings must be open for this step.
  typeMenu?: boolean
  popover: {
    title: string
    description: string
    side?: 'top' | 'right' | 'bottom' | 'left'
    align?: 'start' | 'center' | 'end'
    popoverClass?: string
  }
}

const TOUR_STEPS: TourStep[] = [
  {
    popover: {
      title: 'Welcome to OuraHub 👋',
      description:
        "Here's a quick tour covering:" +
        '<ul>' +
        '<li>OuraHub controls</li>' +
        '<li>Launching apps / tools</li>' +
        '<li>Creating prototypes and their versions</li>' +
        '</ul>' +
        'It will take about a minute.',
    },
  },
  {
    element: '[data-intro-tour-id="top-bar-actions"]',
    popover: {
      title: 'OuraHub controls',
      description:
        'The top bar gives you quick access to documentation, settings and your permissions.\n\n' +
        'The documentation chip updates based on the currently open view, app or tool.\n\n' +
        'When an app / tool is open, the help button color shows which version of it is running.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: '[data-intro-tour-id="side-bar"]',
    popover: {
      title: 'Navigation sidebar',
      description: 'Quickly access your pinned apps, or apps from a ready-made collection.',
      side: 'right',
      align: 'center',
    },
  },
  {
    element: '[data-intro-tour-id="catalog"]',
    popover: {
      title: 'Apps and Tools Catalog',
      description:
        'The catalog is a searchable listing of existing apps and tools.\n\n' +
        'You can only access the apps / tools that have a public version and you have the required permissions for.',
      popoverClass: 'ourahub-tour ourahub-tour-centered',
    },
  },
  {
    element: '[data-intro-tour-id="proto-creator-card"]',
    popover: {
      title: 'Creating a prototype',
      description:
        'You can use the <strong>Prototype Starter</strong> app to create new internal apps, tools and mobile app prototypes.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-intro-tour-id="proto-creator-launch"]',
    popover: {
      title: 'Launching an app / tool',
      description:
        'You can click <strong>Launch application</strong> to open the public version of the app inside OuraHub.\n\n' +
        'We will not launch <strong>Prototype Starter</strong> on this intro tour. Feel free to try it yourself afterwards!',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-intro-tour-id="proto-creator-info"]',
    popover: {
      title: 'Opening app / tool info',
      description: "Clicking on the info icon opens the app's <strong>information dialog</strong> — let's take a look.",
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-intro-tour-id="info-dialog"]',
    demo: true,
    demoView: 'info',
    popover: {
      title: 'App / tool info dialog',
      description:
        "The app info shows what the app does, which versions & environments it's available in, its required permissions and more.\n\n" +
        "If you do not have access rights for the app / tool you'll find a link here to request them from ServiceNow.",
      side: 'left',
      align: 'start',
    },
  },
  {
    element: '[data-intro-tour-id="maintainers"]',
    demo: true,
    demoView: 'info',
    popover: {
      title: 'App / tool maintainers',
      description:
        'Every project has <strong>maintainers</strong> (the owner and collaborators). Until a public version is selected, only they can access a prototype.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-intro-tour-id="edit-settings"]',
    demo: true,
    demoView: 'info',
    popover: {
      title: 'App / tool settings',
      description:
        'Maintainers can edit the settings of the app / tool and manage the versions and select the public version.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: '[data-intro-tour-id="settings-dialog"]',
    demo: true,
    typeMenu: true,
    popover: {
      title: 'App / tool type',
      description:
        'The app / tool type can be changed in the settings.\n\n' +
        '<strong>A standalone internal API service</strong> can be utilized by other apps and tools.\n\n' +
        '<strong>A standalone internal MCP service</strong> can be added to AI tools that support MCP servers.\n\n' +
        '<strong>A web app with optional API backend</strong> will be opened inside the OuraHub interface and is the most common type.\n\n' +
        '<strong>A web app with server-side rendering</strong> will be opened in a new window and serve as a standalone application.\n\n',
      side: 'left',
      align: 'start',
    },
  },
  {
    element: '[data-intro-tour-id="publish-timeline"]',
    demo: true,
    popover: {
      title: 'App / tool versions',
      description:
        "A newly created prototype automatically gets a private <strong>Development</strong> version. Only the app's maintainers can access it.\n\n" +
        '<strong>Selecting a public version</strong> makes that version available to all users, after a review. There can only be one public version at a time.\n\n' +
        'Each version runs in its own environment, which defines the Oura mobile app data it can access: <strong>Development</strong> uses Debug & Feature app data, <strong>Experimental</strong> uses Staging & Experimental app data and <strong>Production</strong> uses Production & Research app data.\n\n' +
        'Creating a <strong>Production</strong> version of an app will need a legal & security assessment.\n\n' +
        '<i>NOTE: Currently creating Production versions of apps is not possible.</i>',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '[data-intro-tour-id="help"]',
    popover: {
      title: "That's it!",
      description:
        'You can replay this intro anytime and discover more helpful information from the <strong>Help</strong> menu. Happy discovering!',
      side: 'bottom',
      align: 'end',
    },
  },
]

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Polls for a selector until it is in the DOM (or the timeout elapses). Tour targets such
// as the environment menu items, the demo dialog contents and the catalog cards render
// asynchronously, so driver must wait for them before it can highlight them.
async function waitForElement(selector: string, timeoutMs: number) {
  const start = Date.now()

  let element = document.querySelector(selector)

  while (!element && Date.now() - start < timeoutMs) {
    await wait(60)
    element = document.querySelector(selector)
  }

  return element
}

async function scrollIntoViewSmoothly(element: Element, timeoutMs = 1000) {
  element.scrollIntoView({ block: 'center', behavior: 'smooth' })

  const start = Date.now()

  let previousTop = element.getBoundingClientRect().top
  let stableChecks = 0

  while (Date.now() - start < timeoutMs) {
    await wait(60)

    const currentTop = element.getBoundingClientRect().top

    // Two consecutive unchanged samples means the smooth scroll has come to rest.
    if (Math.abs(currentTop - previousTop) < 1) {
      if (++stableChecks >= 2) {
        break
      }
    } else {
      stableChecks = 0
    }

    previousTop = currentTop
  }
}

export const useIntroTourStore = defineStore('IntroTour', () => {
  const router = useRouter()
  const firestore = useFirestore()
  const appStore = useAppStore()
  const servicesStore = useServicesStore()
  const ouraAuth = useOuraAuth()

  const tourActive = ref(false)
  const demoSettingsOpen = ref(false)
  const demoView = ref<'info' | 'settings'>('settings')
  const demoTypeMenuOpen = ref(false)

  let driverObj: DriverInstance | undefined

  // For rendering a mocked ServiceDetails dialog
  const demoService = computed<Service>(() => ({
    id: 'example-prototype',
    name: 'Example Prototype App',
    icon: 'mdi-flask-outline',
    contact: '#help-oura-tooling',
    summary: 'An example prototype app created with Proto Starter.',
    category: servicesStore.categoriesList?.core?.[0]?.name ?? '',
    description: 'An example prototype app used to illustrate the OuraHub intro tour.',
    context: 'web-service-spa',
    owner: ouraAuth.user.value?.email ?? 'you@ouraring.com',
    editors: ['collaborator@ouraring.com'],
    public: 'sandbox',
    proto: true,
    entry: { sandbox: '#' },
  }))

  const demoCategory = computed<Category>(
    () =>
      servicesStore.getCategory(demoService.value) ?? {
        name: 'Prototype',
        info: '',
        icon: 'mdi-flask-outline',
        color: 'grey',
      },
  )

  // Opens/closes the dropdown and demo dialog for a step, then waits for the target to exist
  // (and settle) before driver measures and highlights it.
  async function applyStepUi(index: number) {
    const step = TOUR_STEPS[index]

    demoSettingsOpen.value = Boolean(step?.demo)
    demoView.value = step?.demoView ?? 'settings'
    demoTypeMenuOpen.value = Boolean(step?.typeMenu)

    await nextTick()

    if (!step?.element) {
      return
    }

    const element = await waitForElement(step.element, 1500)

    // Let the dialog open transition finish so driver measures the final position.
    if (step.demo) {
      await wait(200)
    }

    // Catalog cards and elements inside the scrollable demo dialog can sit below the fold,
    // so smoothly bring them into view.
    if (element) {
      await scrollIntoViewSmoothly(element)
    }
  }

  function resetUi() {
    tourActive.value = false
    demoSettingsOpen.value = false
    demoTypeMenuOpen.value = false
  }

  async function persistSeen() {
    const userId = ouraAuth.user.value?.id

    if (!userId) {
      return
    }

    await setDoc(
      doc(firestore, 'account-prefs', userId),
      { hasSeenIntro: true, introSeenAt: Date.now() },
      { merge: true },
    ).catch(() => undefined)
  }

  function createDriver(): DriverInstance {
    return driver({
      showProgress: true,
      allowClose: true,
      // Only the close (X) button, Skip and Done end the tour — swallow overlay clicks so a
      // stray click outside the popover can't accidentally dismiss it.
      overlayClickBehavior: () => undefined,
      // Blocks interaction with the highlighted element too, so nothing in the app
      // (including the demo settings dialog) can be clicked while the tour runs.
      disableActiveInteraction: true,
      overlayColor: '#0d0f13',
      stagePadding: 6,
      stageRadius: 8,
      popoverClass: 'ourahub-tour',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      progressText: '{{current}} / {{total}}',
      steps: TOUR_STEPS.map((step) => ({ element: step.element, popover: step.popover })),
      // Add a "Skip" button to the first step that jumps to the last (replay) step.
      onPopoverRender: (popover) => {
        if (driverObj?.getActiveIndex() !== 0) {
          return
        }

        popover.previousButton.style.display = 'none'

        const skipButton = document.createElement('button')
        skipButton.type = 'button'
        skipButton.innerText = 'Skip tour'
        skipButton.className = 'ourahub-tour-skip'
        skipButton.addEventListener('click', async () => {
          const lastIndex = TOUR_STEPS.length - 1
          await applyStepUi(lastIndex)
          driverObj?.moveTo(lastIndex)
        })

        popover.footerButtons.prepend(skipButton)
      },
      // Custom nav handlers so we can open the dropdown/dialog (async) before moving.
      onNextClick: async () => {
        const index = driverObj?.getActiveIndex() ?? 0

        if (index >= TOUR_STEPS.length - 1) {
          driverObj?.destroy()
          return
        }

        await applyStepUi(index + 1)
        driverObj?.moveNext()
      },
      onPrevClick: async () => {
        const index = driverObj?.getActiveIndex() ?? 0

        if (index <= 0) {
          return
        }

        await applyStepUi(index - 1)
        driverObj?.movePrevious()
      },
      onCloseClick: () => driverObj?.destroy(),
      onDestroyed: () => {
        resetUi()
        void persistSeen()
      },
    })
  }

  async function startTour() {
    if (tourActive.value) {
      return
    }

    tourActive.value = true

    // Clear any active catalog filters so the Prototype Starter card the tour anchors to is
    // actually rendered (a leftover search / category filter could otherwise hide it).
    appStore.searchOrTask = ''
    appStore.selectedCategories = []

    // The tour anchors to the persistent shell chrome + catalog, so make sure we are home.
    if (router.currentRoute.value.name !== 'home') {
      await router.push({ name: 'home', params: { env: ouraAuth.appEnv.value } }).catch(() => undefined)
    }

    await applyStepUi(0)

    driverObj = createDriver()
    driverObj.drive()
  }

  return {
    tourActive,
    demoSettingsOpen,
    demoView,
    demoTypeMenuOpen,
    demoService,
    demoCategory,
    startTour,
  }
})

// Reactive refs (destructuring the store directly would lose reactivity).
export function useIntroTour() {
  const store = useIntroTourStore()
  const { tourActive, demoSettingsOpen, demoView, demoTypeMenuOpen, demoService, demoCategory } = storeToRefs(store)

  return {
    tourActive,
    demoSettingsOpen,
    demoView,
    demoTypeMenuOpen,
    demoService,
    demoCategory,
    startTour: store.startTour,
  }
}
